// One option presented in the key combobox. The stored `keyFormula` is the
// keypath (resolvable with MIOManagedObject.valueForKeyPath) corresponding to
// the parent group's matchMode (0 = byDocument, 1 = byLine).
// byLink applies to "By Link" groups (bank movement document links); when absent the
// document path is used, prefixed with document. by the caller.
type AccountingJournalConditionKeyOption = { key:string, label:string, byDoc:string|null, byLine:string|null, byLink?:string|null };

class AccountingJournalTemplateConditionCell extends UITableViewCell
{
    // Key options per event type (domain).
    // Values must match AccountingEventType in AccountingJournalEnums.swift:
    // 1 = Sales Invoice, 2 = Purchase Invoice, 3 = POS Receipt (archived ticket),
    // 4 = Archived Invoice, 5 = Bank Movement
    private static keyOptionsByEventType: { [key: number]: AccountingJournalConditionKeyOption[] } = {
        1: [
            { key:"taxRegime",          label:"TAX_REGIME",          byDoc:"legalEntity.taxRegimeType",          byLine:"document.legalEntity.taxRegimeType" },
            { key:"vat",                label:"VAT",                 byDoc:"vat.identifier",                     byLine:"vat.identifier" },
            { key:"lineClassification", label:"LINE_CLASSIFICATION", byDoc:null,                                 byLine:"lineClassification.identifier" },
            // Only invoice lines carry a VAT factor: "VAT factor >= 0" skips title lines (sections)
            { key:"vatFactor",          label:"VAT_FACTOR",          byDoc:null,                                 byLine:"vatFactor" },
            // The line's additional / retention tax, matched by id like the VAT
            { key:"legalTax",           label:"BUSINESS_TAX",        byDoc:null,                                 byLine:"legalTax.identifier" }
        ],
        2: [
            { key:"taxRegime",          label:"TAX_REGIME",          byDoc:"legalEntity.taxRegimeType",          byLine:"document.legalEntity.taxRegimeType" },
            { key:"vat",                label:"VAT",                 byDoc:"vat.identifier",                     byLine:"vat.identifier" },
            { key:"lineClassification", label:"LINE_CLASSIFICATION", byDoc:null,                                 byLine:"lineClassification.identifier" },
            // Only invoice lines carry a VAT factor: "VAT factor >= 0" skips title lines (sections)
            { key:"vatFactor",          label:"VAT_FACTOR",          byDoc:null,                                 byLine:"vatFactor" },
            // The line's additional / retention tax, matched by id like the VAT
            { key:"legalTax",           label:"BUSINESS_TAX",        byDoc:null,                                 byLine:"legalTax.identifier" }
        ],
        3: [
            { key:"taxRegime",          label:"TAX_REGIME",          byDoc:"legalEntity.taxRegimeType",          byLine:"document.legalEntity.taxRegimeType" },
            { key:"vat",                label:"VAT",                 byDoc:"vat.identifier",                     byLine:"vat.identifier" },
            { key:"lineClassification", label:"LINE_CLASSIFICATION", byDoc:null,                                 byLine:"lineClassification.identifier" },
            // Only invoice lines carry a VAT factor: "VAT factor >= 0" skips title lines (sections)
            { key:"vatFactor",          label:"VAT_FACTOR",          byDoc:null,                                 byLine:"vatFactor" },
            // The line's additional / retention tax, matched by id like the VAT
            { key:"legalTax",           label:"BUSINESS_TAX",        byDoc:null,                                 byLine:"legalTax.identifier" }
        ],
        4: [
            { key:"taxRegime",          label:"TAX_REGIME",          byDoc:"legalEntity.taxRegimeType",          byLine:"document.legalEntity.taxRegimeType" },
            { key:"vat",                label:"VAT",                 byDoc:"vat.identifier",                     byLine:"vat.identifier" },
            { key:"lineClassification", label:"LINE_CLASSIFICATION", byDoc:null,                                 byLine:"lineClassification.identifier" },
            // Only invoice lines carry a VAT factor: "VAT factor >= 0" skips title lines (sections)
            { key:"vatFactor",          label:"VAT_FACTOR",          byDoc:null,                                 byLine:"vatFactor" },
            // The line's additional / retention tax, matched by id like the VAT
            { key:"legalTax",           label:"BUSINESS_TAX",        byDoc:null,                                 byLine:"legalTax.identifier" }
        ],
        5: [
            { key:"concept",          label:"CONCEPT",            byDoc:"concept",              byLine:"document.concept",         byLink:"document.concept" },
            { key:"beneficiaryName",  label:"BENEFICIARY_NAME",   byDoc:"beneficiaryName",      byLine:"document.beneficiaryName", byLink:"document.beneficiaryName" },
            { key:"beneficiaryIBAN",  label:"BENEFICIARY_IBAN",   byDoc:"beneficiaryIBAN",      byLine:"document.beneficiaryIBAN", byLink:"document.beneficiaryIBAN" },
            // By Link: the link's own amount (signed like the movement); By Document: the movement's
            { key:"amount",           label:"AMOUNT",             byDoc:"amount",               byLine:"document.amount",          byLink:"amount" },
            // Entity the link points to: SalesInvoice, LicenseInvoice, PurchaseInvoice, BankMovement, BankMovementConcept
            { key:"linkedEntityName", label:"LINKED_ENTITY_TYPE", byDoc:null,                   byLine:null,                       byLink:"linkedEntityName" },
            // Number of document links of the movement (0 = unlinked)
            { key:"linkCount",        label:"LINK_COUNT",         byDoc:"documentLink.@count",  byLine:null,                       byLink:"document.documentLink.@count" }
        ],
        // 100 = ANALYTIC ACCOUNT pseudo-domain (AccountingJournalTemplateViewController.analyticAccountDomain):
        // the template runs against any source tagged with the account, so it
        // offers the bank-movement keys plus the invoice ones.
        100: [
            { key:"concept",          label:"CONCEPT",            byDoc:"concept",              byLine:"document.concept",         byLink:"document.concept" },
            { key:"beneficiaryName",  label:"BENEFICIARY_NAME",   byDoc:"beneficiaryName",      byLine:"document.beneficiaryName", byLink:"document.beneficiaryName" },
            { key:"beneficiaryIBAN",  label:"BENEFICIARY_IBAN",   byDoc:"beneficiaryIBAN",      byLine:"document.beneficiaryIBAN", byLink:"document.beneficiaryIBAN" },
            { key:"amount",           label:"AMOUNT",             byDoc:"amount",               byLine:"document.amount",          byLink:"amount" },
            { key:"linkedEntityName", label:"LINKED_ENTITY_TYPE", byDoc:null,                   byLine:null,                       byLink:"linkedEntityName" },
            { key:"linkCount",        label:"LINK_COUNT",         byDoc:"documentLink.@count",  byLine:null,                       byLink:"document.documentLink.@count" },
            { key:"taxRegime",          label:"TAX_REGIME",          byDoc:"legalEntity.taxRegimeType",          byLine:"document.legalEntity.taxRegimeType" },
            { key:"vat",                label:"VAT",                 byDoc:"vat.identifier",                     byLine:"vat.identifier" },
            { key:"lineClassification", label:"LINE_CLASSIFICATION", byDoc:null,                                 byLine:"lineClassification.identifier" },
            { key:"vatFactor",          label:"VAT_FACTOR",          byDoc:null,                                 byLine:"vatFactor" },
            { key:"legalTax",           label:"BUSINESS_TAX",        byDoc:null,                                 byLine:"legalTax.identifier" }
        ]
    };

    // Keys whose value should be selected from a combobox instead of free text
    private static selectionKeys: string[] = ["taxRegime", "vat", "lineClassification", "legalTax"];

    private keyFormatComboBox:MUIComboBox = null;
    private operationComboBox:MUIComboBox = null;
    private valueFormulaTextField:MUITextField = null;
    private valueFormatComboBox:MUIComboBox = null;

    private _eventType:number = -1;
    private _keyOptions:AccountingJournalConditionKeyOption[] = [];
    private _taxRegimeList:TaxRegimeType[] = [];
    private _taxList:Tax[] = [];
    private _legalTaxList:LegalTax[] = [];
    private _classificationList:DBDocumentLineClassification[] = [];
    private _activeSelectionKey:string = null;

    awakeFromHTML()
    {
        this.keyFormatComboBox = MUIOutlet(this, "key-format-cb", "MUIComboBox");
        if (this.keyFormatComboBox) this.keyFormatComboBox.setOnChangeAction(this, function(this:AccountingJournalTemplateConditionCell, control:MUIComboBox, value:string){
            let index = parseInt(value);
            let option = (index >= 0 && this._keyOptions[index] != null) ? this._keyOptions[index] : null;
            if (this._item != null) this._item.keyFormula = option != null ? this.keypathForOption(option) : null;
            this.updateValueControlVisibility(option != null ? option.key : null);
        });

        this.operationComboBox = MUIOutlet(this, "operation-cb", "MUIComboBox");
        if (this.operationComboBox) this.operationComboBox.setOnChangeAction(this, function(this:AccountingJournalTemplateConditionCell, control:MUIComboBox, value:string){
            if (this._item != null) this._item.operation = parseInt(value);
        });

        this.valueFormulaTextField = MUIOutlet(this, "value-formula-tf", "MUITextField");
        if (this.valueFormulaTextField) this.valueFormulaTextField.setOnChangeText(this, function(this:AccountingJournalTemplateConditionCell, control:MUITextField, value:string){
            if (this._item != null) this._item.valueFormula = value;
        });

        this.valueFormatComboBox = MUIOutlet(this, "value-format-cb", "MUIComboBox");
        if (this.valueFormatComboBox) {
            this.valueFormatComboBox.setOnChangeAction(this, function(this:AccountingJournalTemplateConditionCell, control:MUIComboBox, value:string){
                if (this._item != null) {
                    let index = parseInt(value);
                    if (index >= 0) {
                        if (this._activeSelectionKey == "taxRegime") {
                            let regime = this._taxRegimeList[index];
                            this._item.valueFormula = regime != null ? String(regime.value) : null;
                        } else if (this._activeSelectionKey == "vat") {
                            let tax = this._taxList[index];
                            this._item.valueFormula = tax != null ? tax.identifier : null;
                        } else if (this._activeSelectionKey == "lineClassification") {
                            let cls = this._classificationList[index];
                            this._item.valueFormula = cls != null ? cls.identifier : null;
                        } else if (this._activeSelectionKey == "legalTax") {
                            let legalTax = this._legalTaxList[index];
                            this._item.valueFormula = legalTax != null ? legalTax.identifier : null;
                        }
                    } else {
                        this._item.valueFormula = null;
                    }
                }
            });
            this.valueFormatComboBox.hidden = true;
        }

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    // Path of an option for a group match mode (0 document, 1 line, 2 link); null when
    // the option does not apply to that mode.
    private static pathForMode(option:AccountingJournalConditionKeyOption, matchMode:number):string|null {
        if (matchMode == 1) return option.byLine;
        if (matchMode == 2) return option.byLink !== undefined ? option.byLink : (option.byDoc != null ? "document." + option.byDoc : null);
        return option.byDoc;
    }

    private currentMatchMode():number {
        if (this._item != null && this._item.parent instanceof AccountingJournalGroupTemplate) {
            return (this._item.parent as AccountingJournalGroupTemplate).matchMode ?? 0;
        }
        return 0;
    }

    private keypathForOption(option:AccountingJournalConditionKeyOption):string {
        let path = AccountingJournalTemplateConditionCell.pathForMode(option, this.currentMatchMode());
        return "{{" + path + "}}";
    }

    set eventType(type:number) {
        if (this._eventType == type) return;
        this._eventType = type;
    }

    private rebuildKeyOptions() {
        let allOptions = AccountingJournalTemplateConditionCell.keyOptionsByEventType[this._eventType] ?? [];
        let matchMode = this.currentMatchMode();
        // Only the options that have a path for the group's match mode
        this._keyOptions = allOptions.filter(function(opt) { return AccountingJournalTemplateConditionCell.pathForMode(opt, matchMode) != null; });
        this.populateKeyOptions();
    }

    private populateKeyOptions() {
        if (this.keyFormatComboBox == null) return;
        this.keyFormatComboBox.removeAllItems();
        this.keyFormatComboBox.addItem(MIOLocalizeString("SELECT", "SELECT"), -1);
        for (let i = 0; i < this._keyOptions.length; i++) {
            this.keyFormatComboBox.addItem(MIOLocalizeString(this._keyOptions[i].label, this._keyOptions[i].label), i);
        }
        this.keyFormatComboBox.selectItem(-1);
        if (this._item != null) this.selectCurrentKey();
    }

    private _item:AccountingJournalGroupConditionTemplate|null = null;
    set item(item:AccountingJournalGroupConditionTemplate)
    {
        this._item = item;
        this.rebuildKeyOptions();
        this.selectCurrentKey();
        if (this.operationComboBox) this.operationComboBox.selectItem(item.operation ?? 0);
        let option = this.optionForCurrentKeyFormula();
        this.updateValueControlVisibility(option != null ? option.key : null);
        this.updateValueDisplay();
    }

    private optionForCurrentKeyFormula():AccountingJournalConditionKeyOption|null {
        if (this._item == null) return null;
        let stored = this._item.keyFormula;
        if (stored == null || stored.length == 0) return null;
        for (let i = 0; i < this._keyOptions.length; i++) {
            let opt = this._keyOptions[i];
            if ("{{" + opt.byDoc + "}}" == stored || "{{" + opt.byLine + "}}" == stored) return opt;
        }
        return null;
    }

    private selectCurrentKey() {
        if (this.keyFormatComboBox == null || this._item == null) return;
        let stored = this._item.keyFormula;
        if (stored == null || stored.length == 0) {
            this.keyFormatComboBox.selectItem(-1);
            return;
        }
        let matchMode = this.currentMatchMode();
        for (let i = 0; i < this._keyOptions.length; i++) {
            let opt = this._keyOptions[i];
            let path = AccountingJournalTemplateConditionCell.pathForMode(opt, matchMode);
            // Current mode's path first; the plain document/line forms keep older rows matched
            if ((path != null && "{{" + path + "}}" == stored) || "{{" + opt.byDoc + "}}" == stored || "{{" + opt.byLine + "}}" == stored) {
                this.keyFormatComboBox.selectItem(i);
                return;
            }
        }
        this.keyFormatComboBox.selectItem(-1);
    }

    private updateValueControlVisibility(key:string) {
        let isSelection = AccountingJournalTemplateConditionCell.selectionKeys.indexOf(key) >= 0;
        this._activeSelectionKey = isSelection ? key : null;

        if (this.valueFormulaTextField) this.valueFormulaTextField.hidden = isSelection;
        if (this.valueFormatComboBox) this.valueFormatComboBox.hidden = !isSelection;

        if (isSelection && key == "taxRegime") {
            this.loadTaxRegimes();
        } else if (isSelection && key == "vat") {
            this.loadTaxes();
        } else if (isSelection && key == "lineClassification") {
            this.loadLineClassifications();
        } else if (isSelection && key == "legalTax") {
            this.loadLegalTaxes();
        }
    }

    private updateValueDisplay() {
        if (this._item == null) return;
        let option = this.optionForCurrentKeyFormula();
        let key = option != null ? option.key : null;
        let isSelection = AccountingJournalTemplateConditionCell.selectionKeys.indexOf(key) >= 0;

        if (!isSelection) {
            if (this.valueFormulaTextField) this.valueFormulaTextField.text = this._item.valueFormula;
        }
        // For selection keys, the combobox is synced after loadTaxRegimes/loadTaxes/loadLineClassifications completes
    }

    private loadTaxRegimes() {
        let sds = [MIOSortDescriptor.sortDescriptorWithKey("title", true)];
        let predicate = MIOPredicate.predicateWithFormat("deletedAt = null");
        DBHelper.queryObjectsWithCompletion("TaxRegimeType", sds, predicate, [], this, function(this:AccountingJournalTemplateConditionCell, objects:TaxRegimeType[]) {
            this._taxRegimeList = objects ?? [];
            if (this.valueFormatComboBox == null) return;
            this.valueFormatComboBox.removeAllItems();
            this.valueFormatComboBox.addItem(MIOLocalizeString("SELECT", "SELECT"), -1);
            for (let i = 0; i < this._taxRegimeList.length; i++) {
                this.valueFormatComboBox.addItem(this._taxRegimeList[i].title, i);
            }
            this.valueFormatComboBox.selectItem(-1);
            if (this._item != null && this._item.valueFormula != null) {
                let savedValue = this._item.valueFormula;
                for (let i = 0; i < this._taxRegimeList.length; i++) {
                    if (String(this._taxRegimeList[i].value) == savedValue) {
                        this.valueFormatComboBox.selectItem(i);
                        break;
                    }
                }
            }
        });
    }

    // The first row of the picker of a VAT, a retention or a classification. A line can have
    // none of them, and a condition can ask for exactly that: "retention, not equal to, none"
    // is every line that carries a retention. So the row says none, not "select".
    private noValueTitle():string {
        return MIOLocalizeString("NONE", "NONE");
    }

    private loadTaxes() {
        let sds = [MIOSortDescriptor.sortDescriptorWithKey("name", true)];
        let predicate = MIOPredicate.predicateWithFormat("deletedAt = null");
        DBHelper.queryObjectsWithCompletion("Tax", sds, predicate, [], this, function(this:AccountingJournalTemplateConditionCell, objects:Tax[]) {
            this._taxList = objects ?? [];
            if (this.valueFormatComboBox == null) return;
            this.valueFormatComboBox.removeAllItems();
            this.valueFormatComboBox.addItem(this.noValueTitle(), -1);
            for (let i = 0; i < this._taxList.length; i++) {
                this.valueFormatComboBox.addItem(this._taxList[i].name, i);
            }
            this.valueFormatComboBox.selectItem(-1);
            if (this._item != null && this._item.valueFormula != null) {
                let savedValue = this._item.valueFormula;
                for (let i = 0; i < this._taxList.length; i++) {
                    if (this._taxList[i].identifier.toUpperCase() == savedValue.toUpperCase()) {
                        this.valueFormatComboBox.selectItem(i);
                        break;
                    }
                }
            }
        });
    }

    private loadLegalTaxes() {
        let sds = [MIOSortDescriptor.sortDescriptorWithKey("name", true)];
        let predicate = MIOPredicate.predicateWithFormat("deletedAt = null");
        DBHelper.queryObjectsWithCompletion("LegalTax", sds, predicate, [], this, function(this:AccountingJournalTemplateConditionCell, objects:LegalTax[]) {
            this._legalTaxList = objects ?? [];
            if (this.valueFormatComboBox == null) return;
            this.valueFormatComboBox.removeAllItems();
            this.valueFormatComboBox.addItem(this.noValueTitle(), -1);
            for (let i = 0; i < this._legalTaxList.length; i++) {
                this.valueFormatComboBox.addItem(this._legalTaxList[i].name, i);
            }
            this.valueFormatComboBox.selectItem(-1);
            if (this._item != null && this._item.valueFormula != null) {
                // ids are stored upper-case by the rule scripts, lower or upper by the screen
                let savedValue = this._item.valueFormula.toUpperCase();
                for (let i = 0; i < this._legalTaxList.length; i++) {
                    if (this._legalTaxList[i].identifier.toUpperCase() == savedValue) {
                        this.valueFormatComboBox.selectItem(i);
                        break;
                    }
                }
            }
        });
    }

    private loadLineClassifications() {
        let sds = [MIOSortDescriptor.sortDescriptorWithKey("title", true)];
        let predicate = MIOPredicate.predicateWithFormat("deletedAt = null");
        DBHelper.queryObjectsWithCompletion("DBDocumentLineClassification", sds, predicate, [], this, function(this:AccountingJournalTemplateConditionCell, objects:DBDocumentLineClassification[]) {
            this._classificationList = objects ?? [];
            if (this.valueFormatComboBox == null) return;
            this.valueFormatComboBox.removeAllItems();
            this.valueFormatComboBox.addItem(this.noValueTitle(), -1);
            for (let i = 0; i < this._classificationList.length; i++) {
                this.valueFormatComboBox.addItem(this._classificationList[i].title, i);
            }
            this.valueFormatComboBox.selectItem(-1);
            if (this._item != null && this._item.valueFormula != null) {
                let savedValue = this._item.valueFormula;
                for (let i = 0; i < this._classificationList.length; i++) {
                    if (this._classificationList[i].identifier == savedValue) {
                        this.valueFormatComboBox.selectItem(i);
                        break;
                    }
                }
            }
        });
    }
}
